<template>
  <div class="send-contract-modal">
    <HMModalHeader
      :title="$t('CONTRACTS.TITLES.SEND_A_CONTRACT')"
      @close="$emit('close')"
    />
    <div v-if="!sendType" class="d-flex flex-row flex-nowrap">
      <section class="selection-card mr-2" @click="sendType = 'internal'">
        <i class="fas fa-user fa-2x c-brand-color"></i>
        <h4 class="mt-3">
          {{ $t('CONTRACTS.TEXT.SEND_TO_EXISTING_STUDENT') }}
        </h4>
      </section>
      <section class="selection-card ml-2" @click="sendType = 'external'">
        <i class="fas fa-external-link-alt fa-2x c-brand-color"></i>
        <h4 class="mt-3">
          {{ $t('CONTRACTS.TEXT.SEND_TO_EXTERNAL_CUSTOMER') }}
        </h4>
      </section>
    </div>
    <ValidationObserver v-else ref="form" v-slot="formData">
      <form @submit.prevent="sendContract">
        <v-select
          class="mb-4"
          v-if="contracts"
          @search="searchContracts"
          v-model="form.contract"
          :options="contracts"
          label="title"
          code="id"
          :placeholder="$t('CONTRACTS.TEXT.SELECT_CONTRACT_TO_SEND')"
        >
          <template v-slot:option="option">
            {{ option.title }}
          </template>
        </v-select>
        <div v-if="sendType === 'external'">
          <HMTextInput
            type="text"
            rules="required"
            :label="$t('CONTRACTS.LABELS.FULL_NAME')"
            v-model="form.full_name"
            name="name"
          />
          <HMTextInput
            type="text"
            rules="required"
            :label="$t('CONTRACTS.LABELS.EMAIL')"
            v-model="form.email"
            name="name"
          />
        </div>
        <div v-else>
          <v-select
            v-if="customers"
            @search="searchCustomers"
            v-model="form.student"
            :options="customers.results"
            label="full_name"
            code="id"
            :placeholder="$t('CONTRACTS.TEXT.FIND_YOUR_CUSTOMER')"
          />
        </div>
        <v-select
          v-if="sendType === 'internal'"
          class="my-4"
          v-model="form.product"
          :options="allProducts"
          :placeholder="$t('CONTRACTS.TEXT.SELECT_OPTIONAL_PRODUCT')"
        />
        <HMModalFooter
          class="mt-3"
          :submitButton="$t('GENERAL.BUTTON.SEND')"
          :disabled="inProgress || formData.invalid"
          :loading="inProgress"
          :formError="formError"
          @close="$emit('close')"
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { debounce, orderBy, map } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import { errorToMessage } from '../../../core/utils/errors';
import { formatObjectForBackend } from '@/core/api/utils/converter';
import { mapGetters } from 'vuex';

export default {
  name: 'SendContractModal',
  components: {
    ValidationObserver,
  },
  computed: {
    ...mapGetters({
      allTeacherProducts: 'product/allTeacherProducts',
    }),
    allProducts() {
      return (this.allTeacherProducts ?? []).map(product => ({
        ...product,
        label: product.product_name,
        value: product.id,
      }));
    },
  },
  data() {
    return {
      sendType: null,
      contracts: null,
      customers: null,
      form: {
        contract: null,
        student: null,
        full_name: null,
        email: null,
        product: null,
      },
      inProgress: false,
      formError: null,
    };
  },
  methods: {
    orderCustomers(customers) {
      map(this.customers.results, customer => {
        customer.full_name = `${customer.full_name} (${customer.email})`;
      });
      customers.results = orderBy(customers.results, ['full_name'], ['asc']);
    },
    searchCustomers: debounce(async function (query) {
      this.customers = await this.$store.dispatch('sale/getSalesCustomers', {
        q: query,
      });
      if (this.customers) this.orderCustomers(this.customers);
    }, 300),
    searchContracts: debounce(async function (query) {
      this.contracts = await this.$store.dispatch('contracts/getAllContracts', {
        q: query,
      });
    }, 300),
    async sendContract() {
      try {
        this.inProgress = true;
        const sentContract = await this.$store.dispatch(
          'contracts/sendContractToSign',
          formatObjectForBackend(this.form)
        );
        console.log('sentContract', sentContract);
        this.$emit('close');
        this.$parent.$emit('onContractSent', sentContract);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to send contract',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
  },
  async mounted() {
    this.contracts = await this.$store.dispatch(
      'contracts/getAllContracts',
      {}
    );
    this.customers = await this.$store.dispatch('sale/getSalesCustomers');
    if (this.customers) this.orderCustomers(this.customers);
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.selection-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem 2rem;
  border: 1px solid $light-gray;
  max-width: 50%;
  text-align: center;
  cursor: pointer;
  border-radius: $border-radius;
  &:hover {
    background-color: darken(white, 3%);
  }
}
</style>
